/**
 * 布置学习（与 iOS AssignView 一致）：新建一个"打开学习内容"的日程（截止 / 时段 / 放进时间段，可以重复），
 * 每次发生服务端自动生成一条学习记录（docs/API.md 2.4）。
 * "只读这几页""怎样算完成"、游戏的题数等设置属于 payload：模块 payload Schema（游戏是 params_schema）里标了 x-assign 的字段，
 * 由通用的设置表单生成（activities/SchemaForm.tsx，docs/插件开发.md 第 4 节）。为了不影响这份内容已有的其他安排，
 * 设置改动时会先复制一份内容（标题带上页码）再布置，原内容保持不变。
 */
import { useEffect, useState } from "react";
import { type Content, ON_TIME_BONUS, run, toast, useCalendar, useChildren, useCurrentChild, useStore, writeEntity } from "../../core";
import { gameOf } from "../../activities/games";
import { activityInfo } from "../../activities/registry";
import { type FormField, type FormValues, pageRangeSuffix, parseFormSchema } from "../../activities/schema-form";
import { SchemaFields } from "../../activities/SchemaForm";
import { C, ChoiceChip, FormCard, ModalHeader, NumberStepper, RowDivider, Sheet, SheetBody, Switch, ValueRow } from "../../ui";
import { useSlots, type When, WhenCard, whenDueAt, whenEvent, whenValid } from "../../components/TaskTime";
import { NO_REPEAT } from "../schedule/repeat";
import { activityName, dayLabel, defaultSlot, isObject, modifiedPayload, payloadOf, settingsObject, slotKey, str } from "./activity";
import { TypeBlock } from "./Library";
import { webModule } from "./modules";

export function AssignSheet({ content, onClose }: { content: Content | null; onClose: () => void }) {
  return (
    <Sheet open={!!content} onClose={onClose} width={560} persistent label="布置学习">
      {content && <AssignForm key={content.id} content={content} onClose={onClose} />}
    </Sheet>
  );
}

function AssignForm({ content, onClose }: { content: Content; onClose: () => void }) {
  const cal = useCalendar();
  const children = useChildren();
  const current = useCurrentChild();
  const [childId, setChildId] = useState<string | null>(current?.id ?? null);
  const child = useStore((s) => (childId ? s.children[childId] : undefined)) ?? current;
  const today = cal.today();

  // 默认当天 20:00 前做完；想指定几点做时选"时段"，或放进某个日程时段
  const [when, setWhen] = useState<When>({ mode: "due", date: today, slot: null, startTime: "17:00", duration: content.estimatedMin ?? 20, dueTime: "20:00", repeat: NO_REPEAT });
  const [reward, setReward] = useState(10);
  const [bonus, setBonus] = useState(ON_TIME_BONUS);
  const [remind, setRemind] = useState(true);
  const [busy, setBusy] = useState(false);
  // payload 快捷设置：字段来自 Schema 的 x-assign，值改在 values 里
  const [fields, setFields] = useState<FormField[]>([]);
  const [values, setValues] = useState<FormValues>(() => settingsObject(content));
  /** 模块给的提示值，如 PDF 的 page_count */
  const [hints, setHints] = useState<Record<string, number>>({});

  const { slots, loaded: slotsLoaded } = useSlots(child);
  const slot = slots.find((x) => slotKey(x) === when.slot) ?? null;

  // 加载完时间段：挂时间段时默认第一个课后时间段
  useEffect(() => {
    if (!slotsLoaded || slot) return;
    const d = defaultSlot(slots);
    if (d) setWhen((w) => ({ ...w, slot: slotKey(d) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slotsLoaded, slots]);

  // 设置表单：原生模块用 payload Schema 里 x-assign 的字段，互动游戏用这个游戏版本的 params_schema
  useEffect(() => {
    let alive = true;
    if (content.activityType === "web_activity") {
      const pkg = payloadOf(content).package;
      const id = isObject(pkg) ? str(pkg.id) : null;
      if (id) {
        gameOf(id, isObject(pkg) ? str(pkg.version) : null)
          .then((g) => alive && setFields(parseFormSchema(g?.paramsSchema, { assignOnly: true })))
          .catch(() => undefined);
      }
    } else {
      setFields(parseFormSchema(activityInfo(content.activityType).schema, { assignOnly: true }));
      webModule(content.activityType)
        .assignHints?.(content)
        .then((h) => alive && setHints(h))
        .catch(() => undefined);
    }
    return () => {
      alive = false;
    };
  }, [content]);

  const dueAt = whenDueAt(cal, when, slot);
  const canSubmit = !!child && whenValid(when, slot);

  const submit = async () => {
    if (!child || !canSubmit) return;
    setBusy(true);
    const a = await run(async () => {
      let contentId = content.id;
      const p = modifiedPayload(content, fields, values);
      const copyTitle = `${content.title}${pageRangeSuffix(fields, values)}`;
      if (p) {
        const copy = await writeEntity("contents", "POST", "contents", {
          folderId: content.folderId ?? undefined,
          tagIds: content.tagIds ?? [],
          title: copyTitle,
          description: content.description ?? undefined,
          activityType: content.activityType,
          payload: p,
          payloadVersion: content.payloadVersion,
          estimatedMin: content.estimatedMin ?? undefined,
        });
        contentId = copy.id;
      }
      const time = whenEvent(when, slot);
      const title = (p ? copyTitle : content.title).slice(0, 50);
      const ev = await writeEntity("scheduleEvents", "POST", `children/${child.id}/events`, {
        ...time,
        title,
        icon: "homework",
        action: { type: "content", contentId },
        pointsReward: reward,
        onTimeBonus: bonus,
      });
      // 没做时提醒：截止前 30 分钟；时段的在开始时
      if (remind) {
        const due = time.timeMode === "due";
        await writeEntity("reminderRules", "POST", `children/${child.id}/reminder-rules`, {
          sourceType: "event",
          sourceId: ev.id,
          offsetsMin: [due ? -30 : 0],
          speakText: due ? "{title}还没有完成哦" : "{title}要开始啦",
          sound: "chime_soft",
          requireAck: true,
          repeatIntervalMin: 2,
          repeatMax: 1,
          enabled: true,
        });
      }
      return ev;
    });
    setBusy(false);
    if (a) {
      toast(`已布置给${child.name}`);
      onClose();
    }
  };

  const sub = [activityName(content.activityType)];
  if (hints.page_count) sub.push(`共 ${hints.page_count} 页`);
  const summary = webModule(content.activityType).summary?.(content);
  if (summary) sub.push(summary);
  if (content.estimatedMin) sub.push(`约 ${content.estimatedMin} 分钟`);

  return (
    <>
      <ModalHeader title="布置学习" actionTitle="布置" actionEnabled={canSubmit} busy={busy} onCancel={onClose} onAction={() => void submit()} />
      <SheetBody gap={12}>
        <div className="learn-detail-head">
          <TypeBlock type={content.activityType} width={56} height={72} radius={8} size={13} />
          <div>
            <h2>{content.title}</h2>
            <p>{sub.join(" · ")}</p>
          </div>
        </div>

        {children.length > 1 && (
          <div className="learn-chips is-center">
            <span className="learn-label">布置给</span>
            {children.map((c) => (
              <ChoiceChip
                key={c.id}
                selected={c.id === child?.id}
                unselectedBg={C.surface}
                onClick={() => {
                  setChildId(c.id);
                  setWhen((w) => ({ ...w, slot: null }));
                }}
              >
                {c.name}
              </ChoiceChip>
            ))}
          </div>
        )}

        <WhenCard child={child} value={when} onChange={setWhen} slots={slots} slotsLoaded={slotsLoaded} />

        <FormCard>
          {fields.length > 0 && (
            <>
              <SchemaFields fields={fields} values={values} onChange={setValues} hints={hints} />
              <RowDivider />
            </>
          )}
          <div className="learn-sub">
            <div className="learn-due-head">
              <span>截止</span>
              <span className="learn-due-value">{dueAt != null ? `${dayLabel(cal, cal.dateString(dueAt))} ${cal.timeString(dueAt)}${bonus > 0 ? ` · 按时完成 +${bonus}` : ""}` : "—"}</span>
            </div>
            <p className="learn-note">{when.mode === "due" ? "截止时间就是上面填的时间" : "时段结束时截止"}</p>
          </div>
          <RowDivider />
          <ValueRow title="完成奖励" height={56}>
            <NumberStepper value={reward} min={0} max={100} onChange={setReward} />
          </ValueRow>
          {dueAt != null && (
            <>
              <RowDivider />
              <ValueRow title="按时完成另加" height={56}>
                <NumberStepper value={bonus} min={0} max={100} onChange={setBonus} />
              </ValueRow>
            </>
          )}
        </FormCard>

        <FormCard>
          <Switch label={when.mode === "due" ? "没做时提醒孩子（截止前 30 分钟）" : "开始时提醒孩子"} checked={remind} onChange={setRemind} />
        </FormCard>
        {modifiedPayload(content, fields, values) && <p className="learn-note">设置和原内容不同，会复制一份内容再布置，原内容保持不变。</p>}
      </SheetBody>
    </>
  );
}
